import {CascadeStepEventCategory, CascadeStepStatus, ResourceGroupId, ResourceGroupPlatform} from "@generated"
import {JobResourceGroupPinLabels} from "@platform/models/state-labels/job-queue-group-labels"

/** One resolved cascade step, as the API delivers it (the JobTaskCascadeStep GraphQL type). */
export type CascadeStep = {
    resourceGroupId: ResourceGroupId
    label: string
    platform: ResourceGroupPlatform
    status: CascadeStepStatus
    events: readonly CascadeStepEvent[]
}

/** One event on a step, for the popover's vertical list (the JobTaskCascadeStepEvent GraphQL type). */
export type CascadeStepEvent = {
    label: string
    observedAt: string
    message?: string | null
    category: CascadeStepEventCategory
}

/** Which platform glyph a step draws — the recognisable Google Cloud product shapes, plus on-prem. */
export type PlatformIcon = "compute-engine" | "cloud-run" | "render-farm"

/**
 * Presentation for a provisioning platform: its glyph, a human label, and a 2-dot mode indicator showing
 * how committed the provisioning is — `dots` = filled count out of 2: spot 0 (preemptible), flex-start 1
 * (queued, not preempted once running), on-demand 2 (immediate, never preempted). `null` for platforms
 * that aren't a Compute Engine mode (Cloud Run, render farm) — their glyph already distinguishes them, so
 * no dots are drawn.
 */
export const PLATFORM: Record<ResourceGroupPlatform, {icon: PlatformIcon; label: string; dots: number | null}> = {
    [ResourceGroupPlatform.ComputeEngineSpot]: {icon: "compute-engine", label: "Compute Engine · spot", dots: 0},
    [ResourceGroupPlatform.ComputeEngineFlexStart]: {icon: "compute-engine", label: "Compute Engine · flex-start", dots: 1},
    [ResourceGroupPlatform.ComputeEngineOnDemand]: {icon: "compute-engine", label: "Compute Engine · on-demand", dots: 2},
    [ResourceGroupPlatform.CloudRun]: {icon: "cloud-run", label: "Cloud Run", dots: null},
    [ResourceGroupPlatform.RenderFarm]: {icon: "render-farm", label: "Render farm (on-prem)", dots: null},
}

/**
 * Colour class + label per step status (colours live in the SCSS): active light blue, completed green,
 * Failed red (a group that ran then was lost after provisioning), Skipped amber (never ran here — stocked
 * out, misconfigured, or the cascade moved past it), pending grey.
 */
export const STATUS: Record<CascadeStepStatus, {class: string; label: string}> = {
    [CascadeStepStatus.Active]: {class: "cm-active", label: "Active"},
    [CascadeStepStatus.Completed]: {class: "cm-completed", label: "Completed"},
    [CascadeStepStatus.Failed]: {class: "cm-failed", label: "Failed"},
    [CascadeStepStatus.Skipped]: {class: "cm-skipped", label: "Skipped"},
    [CascadeStepStatus.Pending]: {class: "cm-pending", label: "Not reached"},
}

/**
 * The GCP service each platform runs on. Stripped from a step's label in the popover header — the step's
 * icon and its mode line already name the service, so repeating it ("Compute Engine GPU (spot)" above a
 * "Compute Engine · spot" line) is noise. The header then reads just the workload + tier, e.g. "GPU (spot)".
 */
const SERVICE_PREFIX: Record<ResourceGroupPlatform, string> = {
    [ResourceGroupPlatform.ComputeEngineSpot]: "Compute Engine",
    [ResourceGroupPlatform.ComputeEngineFlexStart]: "Compute Engine",
    [ResourceGroupPlatform.ComputeEngineOnDemand]: "Compute Engine",
    [ResourceGroupPlatform.CloudRun]: "Cloud Run",
    [ResourceGroupPlatform.RenderFarm]: "Render Farm",
}

/** A step's popover title: its resource-group label with the redundant GCP-service prefix removed. */
export function stepShortLabel(step: CascadeStep): string {
    const full = JobResourceGroupPinLabels.get(step.resourceGroupId)?.label ?? step.label
    const prefix = `${SERVICE_PREFIX[step.platform]} `
    return full.startsWith(prefix) ? full.slice(prefix.length) : full
}

/** Colour class per event category — the dot beside each event row in the popover. */
export const CATEGORY_CLASS: Record<CascadeStepEventCategory, string> = {
    [CascadeStepEventCategory.Neutral]: "cm-cat-neutral",
    [CascadeStepEventCategory.Active]: "cm-cat-active",
    [CascadeStepEventCategory.Success]: "cm-cat-success",
    [CascadeStepEventCategory.Warning]: "cm-cat-warning",
    [CascadeStepEventCategory.Error]: "cm-cat-error",
}
